import React, { useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import { usePortfolioContext } from '../contexts/PortfolioContext'
import ConfirmDeleteModal from '../components/ConfirmDeleteModal'
import toast from 'react-hot-toast'

const API_KEYS_QUERY = gql`
  query ApiKeys {
    apiKeys {
      id
      name
      allowedPortfolioIds
      createdAt
      lastUsedAt
    }
  }
`

const CREATE_API_KEY_MUTATION = gql`
  mutation CreateApiKey($name: String!, $allowedPortfolioIds: [ID!]) {
    createApiKey(name: $name, allowedPortfolioIds: $allowedPortfolioIds) {
      apiKey {
        id
        name
        allowedPortfolioIds
        createdAt
      }
      plainKey
    }
  }
`

const DELETE_API_KEY_MUTATION = gql`
  mutation DeleteApiKey($id: ID!) {
    deleteApiKey(id: $id)
  }
`

const ApiKeysPage = () => {
  const { portfolios } = usePortfolioContext()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [selectedPortfolios, setSelectedPortfolios] = useState([])
  const [restrictToPortfolios, setRestrictToPortfolios] = useState(false)
  const [createdKey, setCreatedKey] = useState(null)
  const [copied, setCopied] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const { data, loading, error, refetch } = useQuery(API_KEYS_QUERY)

  const [createApiKey, { loading: creating }] = useMutation(CREATE_API_KEY_MUTATION, {
    onCompleted: (data) => {
      setCreatedKey(data.createApiKey.plainKey)
      setNewKeyName('')
      setSelectedPortfolios([])
      setRestrictToPortfolios(false)
      refetch()
      toast.success('API key created')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to create API key')
    }
  })

  const [deleteApiKey] = useMutation(DELETE_API_KEY_MUTATION, {
    onCompleted: () => {
      refetch()
      toast.success('API key deleted')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to delete API key')
    }
  })

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key')
      return
    }
    const allowedIds = restrictToPortfolios && selectedPortfolios.length > 0 
      ? selectedPortfolios 
      : null
    await createApiKey({ variables: { name: newKeyName.trim(), allowedPortfolioIds: allowedIds } })
  }

  const handleDelete = (id, name) => {
    setDeleteTarget({ id, name })
  }

  const confirmDelete = async () => {
    if (deleteTarget) {
      await deleteApiKey({ variables: { id: deleteTarget.id } })
      setDeleteTarget(null)
    }
  }

  const handleCopyKey = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey)
      setCopied(true)
      toast.success('API key copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleCloseCreatedKey = () => {
    setCreatedKey(null)
    setShowCreateModal(false)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateStr
    }
  }

  const getPortfolioNames = (ids) => {
    if (!ids || ids.length === 0) return 'All portfolios'
    const names = ids.map(id => {
      const p = portfolios?.find(p => p.id === id)
      return p?.name || id.slice(0, 8)
    })
    return names.join(', ')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">Failed to load API keys: {error.message}</p>
          </div>
        </div>
      </div>
    )
  }

  const apiKeys = data?.apiKeys || []

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Keys</h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Create and manage API keys for programmatic access to your portfolio data.
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Create API Key
            </button>
          </div>

          <div className="px-6 py-4">
            {apiKeys.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">No API keys</h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Create your first API key to access your portfolio data programmatically.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                          {key.name}
                        </h3>
                        <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 space-y-1">
                          <p>
                            <span className="font-medium">Access:</span> {getPortfolioNames(key.allowedPortfolioIds)}
                          </p>
                          <p>
                            <span className="font-medium">Created:</span> {formatDate(key.createdAt)}
                          </p>
                          <p>
                            <span className="font-medium">Last used:</span> {formatDate(key.lastUsedAt)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(key.id, key.name)}
                        className="ml-4 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Usage</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Use your API key in the <code className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Authorization</code> header:
            </p>
            <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs overflow-x-auto">
              <code>Authorization: Bearer mr_your_api_key_here</code>
            </pre>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              View the{' '}
              <a 
                href="https://api.moonready.me/v1/openapi.yaml" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                REST API Schema (OpenAPI)
              </a>
              {' '}for available endpoints and request/response formats.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <ConfirmDeleteModal
          title="Delete API Key"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This cannot be undone and any applications using this key will stop working.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          confirmText="Delete Key"
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" onClick={handleCloseCreatedKey} />
            
            <div className="relative bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
              {createdKey ? (
                // Show created key
                <div className="px-6 py-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="ml-3 text-lg font-medium text-gray-900 dark:text-gray-100">API Key Created</h3>
                  </div>
                  
                  <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Important:</strong> Copy your API key now. You won't be able to see it again.
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your API Key</label>
                    <div className="flex">
                      <input
                        type="text"
                        value={createdKey}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                      />
                      <button
                        onClick={handleCopyKey}
                        className={`px-4 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md text-sm font-medium transition-colors ${
                          copied
                            ? 'bg-green-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleCloseCreatedKey}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Done
                  </button>
                </div>
              ) : (
                // Create form
                <form onSubmit={handleCreate}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Create API Key</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Give your API key a name to help you identify it later.
                    </p>
                  </div>

                  <div className="px-6 py-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        placeholder="e.g., Trading Bot, Portfolio Sync"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={restrictToPortfolios}
                          onChange={(e) => setRestrictToPortfolios(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Restrict to specific portfolios
                        </span>
                      </label>
                    </div>

                    {restrictToPortfolios && portfolios && portfolios.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Select portfolios
                        </label>
                        <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2">
                          {portfolios.map((p) => (
                            <label key={p.id} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedPortfolios.includes(p.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedPortfolios([...selectedPortfolios, p.id])
                                  } else {
                                    setSelectedPortfolios(selectedPortfolios.filter(id => id !== p.id))
                                  }
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{p.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating || !newKeyName.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creating ? 'Creating...' : 'Create Key'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ApiKeysPage
